import {
  INCOMING_BOOKING_STATUS_LABELS,
  INCOMING_RESPONSE_STATUS_LABELS,
  INVOICE_SCENARIO_LABELS,
  type IncomingInvoiceSummary,
  incomingBookingStatusSchema,
  incomingResponseStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  DateInput,
  EmptyState,
  FormField,
  Input,
  MoneyText,
  Select,
  useToast,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Download, Inbox } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import {
  BookingStatusBadge,
  DaysLeftBadge,
  IncomingResponseBadge,
  InvoicingPage,
  useInvoicingCrumb,
  useRefreshInvoicing,
} from "../../../../features/invoicing/shared.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const searchSchema = z.object({
  ...companySearchShape,
  responseStatus: incomingResponseStatusSchema.optional().catch(undefined),
  bookingStatus: incomingBookingStatusSchema.optional().catch(undefined),
  from: date,
  to: date,
  q: z.string().max(100).optional().catch(undefined),
});
type InboxSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/faturalar/gelen-kutusu/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: InboxPage,
});

function InboxPage() {
  const { t } = useTranslation();
  const crumb = useInvoicingCrumb("/faturalar");
  const search = Route.useSearch();
  return (
    <InvoicingPage
      title={t("menu.invoiceInbox")}
      description={t("invoicing.inbox.description")}
      breadcrumb={[crumb, { label: t("menu.invoiceInbox") }]}
      actions={({ companyId }) => <FetchButton companyId={companyId} />}
    >
      {({ companyId }) => <InboxList companyId={companyId} search={search} />}
    </InvoicingPage>
  );
}

function FetchButton({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInvoicing();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      loading={busy}
      onClick={() => {
        setBusy(true);
        api
          .POST("/incoming-invoices/fetch", { body: { companyId } })
          .then(async ({ data }) => {
            await refresh(companyId);
            toast({
              tone: "success",
              title: t("invoicing.inbox.fetched", {
                fetched: data!.fetched,
                created: data!.created,
                matched: data!.matched,
              }),
            });
          })
          .catch((error: unknown) => toast({ tone: "danger", title: errorMessage(error) }))
          .finally(() => setBusy(false));
      }}
    >
      <Download aria-hidden="true" />
      {t("invoicing.inbox.fetchNow")}
    </Button>
  );
}

function InboxList({ companyId, search }: { companyId: string; search: InboxSearch }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const [searchText, setSearchText] = useState(search.q ?? "");
  const setFilters = (patch: Partial<InboxSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== searchText.trim()) setFilters({ q: searchText.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const query = {
    companyId,
    limit: 25,
    ...(search.responseStatus ? { responseStatus: search.responseStatus } : {}),
    ...(search.bookingStatus ? { bookingStatus: search.bookingStatus } : {}),
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
    ...(search.q ? { search: search.q } : {}),
  };
  const pages = useCursorPages(JSON.stringify(query));
  const list = useQuery({
    queryKey: queryKeys.invoicing(companyId, "incoming", { ...query, cursor: pages.cursor ?? null }),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await api.GET("/incoming-invoices", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
  });

  const columns: DataTableColumn<IncomingInvoiceSummary>[] = [
    {
      id: "invoiceNo",
      header: t("invoicing.fields.invoiceNo"),
      hideable: false,
      cell: (row) => (
        <Link
          to="/faturalar/gelen-kutusu/$id"
          params={{ id: row.id }}
          className="font-mono text-primary underline-offset-4 hover:underline"
        >
          {row.invoiceNo}
        </Link>
      ),
      exportValue: (row) => row.invoiceNo,
    },
    {
      id: "sender",
      header: t("invoicing.inbox.sender"),
      cell: (row) => (
        <span className="grid">
          {row.senderTitle}
          <span className="font-mono text-xs text-muted-foreground">{row.senderTaxId}</span>
        </span>
      ),
      exportValue: (row) => `${row.senderTitle} (${row.senderTaxId})`,
    },
    {
      id: "issueDate",
      header: t("invoicing.fields.issueDate"),
      cell: (row) => <span className="tabular">{formatDate(row.issueDate)}</span>,
      exportValue: (row) => row.issueDate,
    },
    {
      id: "scenario",
      header: t("invoicing.fields.scenario"),
      cell: (row) => INVOICE_SCENARIO_LABELS[row.scenario],
      exportValue: (row) => INVOICE_SCENARIO_LABELS[row.scenario],
    },
    {
      id: "payableAmount",
      header: t("invoicing.fields.payableAmount"),
      align: "right",
      cell: (row) => <MoneyText value={row.payableAmount} className="font-semibold" />,
      exportValue: (row) => row.payableAmount,
    },
    {
      id: "counterparty",
      header: t("invoicing.inbox.matchedCounterparty"),
      cell: (row) =>
        row.counterpartyId ? (
          <Link
            to="/cari/$id"
            params={{ id: row.counterpartyId }}
            className="text-primary underline-offset-4 hover:underline"
          >
            {row.counterpartyName}
          </Link>
        ) : (
          <span className="text-warning">{t("invoicing.inbox.noCounterparty")}</span>
        ),
      exportValue: (row) => row.counterpartyName ?? "",
    },
    {
      id: "responseStatus",
      header: t("invoicing.inbox.responseStatus"),
      cell: (row) => (
        <span className="flex flex-wrap gap-1">
          <IncomingResponseBadge status={row.responseStatus} />
          {row.responseStatus === "PENDING" && row.daysLeft !== null ? (
            <DaysLeftBadge daysLeft={row.daysLeft} />
          ) : null}
        </span>
      ),
      exportValue: (row) => INCOMING_RESPONSE_STATUS_LABELS[row.responseStatus],
    },
    {
      id: "bookingStatus",
      header: t("invoicing.inbox.bookingStatus"),
      cell: (row) => (
        <span className="flex flex-wrap gap-1">
          <BookingStatusBadge status={row.bookingStatus} />
          {row.bookedInvoiceId ? (
            <Link
              to="/faturalar/$id"
              params={{ id: row.bookedInvoiceId }}
              className="text-xs text-primary underline-offset-4 hover:underline"
            >
              {t("invoicing.inbox.bookedInvoice")}
            </Link>
          ) : null}
        </span>
      ),
      exportValue: (row) => INCOMING_BOOKING_STATUS_LABELS[row.bookingStatus],
    },
  ];

  return (
    <DataTable
      caption={t("menu.invoiceInbox")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<Inbox />}
          title={t("invoicing.inbox.emptyTitle")}
          description={t("invoicing.inbox.emptyDescription")}
        />
      }
      filters={
        <>
          <FormField label={t("invoicing.inbox.responseStatus")} className="w-48">
            {(control) => (
              <Select
                {...control}
                value={search.responseStatus ?? ""}
                onChange={(event) =>
                  setFilters({
                    responseStatus:
                      event.target.value === ""
                        ? undefined
                        : incomingResponseStatusSchema.parse(event.target.value),
                  })
                }
              >
                <option value="">{t("common.all")}</option>
                {incomingResponseStatusSchema.options.map((status) => (
                  <option key={status} value={status}>
                    {INCOMING_RESPONSE_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("invoicing.inbox.bookingStatus")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.bookingStatus ?? ""}
                onChange={(event) =>
                  setFilters({
                    bookingStatus:
                      event.target.value === ""
                        ? undefined
                        : incomingBookingStatusSchema.parse(event.target.value),
                  })
                }
              >
                <option value="">{t("common.all")}</option>
                {incomingBookingStatusSchema.options.map((status) => (
                  <option key={status} value={status}>
                    {INCOMING_BOOKING_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("invoicing.list.from")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={search.from ?? null}
                onValueChange={(value) => setFilters({ from: value ?? undefined })}
              />
            )}
          </FormField>
          <FormField label={t("invoicing.list.to")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={search.to ?? null}
                onValueChange={(value) => setFilters({ to: value ?? undefined })}
              />
            )}
          </FormField>
          <FormField label={t("common.search")} className="w-56">
            {(control) => (
              <Input
                {...control}
                type="search"
                value={searchText}
                placeholder={t("invoicing.inbox.searchPlaceholder")}
                onChange={(event) => setSearchText(event.target.value)}
              />
            )}
          </FormField>
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
